import { useState } from "react";
import { TypedFilter } from "odata-query";
import { taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { IApiRequest, IIssueTemplateResponse } from "@miplatform/types";

export const useIssueTemplates = ({
  initialFilter,
}: UseIssueTemplatesProps = {}) => {
  const [issueTemplates, setIssueTemplates] = useState<
    IIssueTemplateResponse[]
  >([]);
  const [recentIssueTemplates, setRecentIssueTemplates] = useState<
    IIssueTemplateResponse[]
  >([]);
  const [refresh, setRefresh] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<TypedFilter<IIssueTemplateResponse>>(
    initialFilter ?? {}
  );

  const stateManager = useComponentState();
  const debouncedSearch = useDebounce(search);

  const getIssueTemplates = ({ top, skip, filters }: IApiRequest = {}) => {
    const combinedFilters = {
      ...filter,
      ...filters,
      name: { contains: debouncedSearch },
    };

    return stateManager.run(async () => {
      return taskService.issueTemplates.getAll({
        top,
        skip,
        sort: "name",
        filters: combinedFilters,
      });
    });
  };

  const getRecentIssueTemplates = ({ sort, top, filters }: IApiRequest) => {
    const combinedFilters = {
      ...filter,
      ...filters,
      name: { contains: debouncedSearch },
    };

    return stateManager.run(async () => {
      return taskService.issueTemplates.getAll({
        sort,
        top,
        filters: combinedFilters,
      });
    });
  };

  const handleDeleteIssueTemplate = (data: IIssueTemplateResponse) => {
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.delete({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} was deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${data.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  const handleCreateIssueTemplate = (data: Partial<IIssueTemplateResponse>) => {
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.create({
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} was created successfully.`,
          },
          failure: {
            text: `Failed to create ${data.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.CREATED) {
        setRefresh((prev) => !prev);
      }
    });
  };

  const handleUpdateIssueTemplate = (data: Partial<IIssueTemplateResponse>) => {
    stateManager.run(async () => {
      const response = await taskService.issueTemplates.update({
        id: data.id,
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} was updated successfully.`,
          },
          failure: {
            text: `Failed to update ${data.name}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setRefresh((prev) => !prev);
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    getIssueTemplates,
    issueTemplates,
    setIssueTemplates,
    getRecentIssueTemplates,
    setRecentIssueTemplates,
    recentIssueTemplates,
    handleDeleteIssueTemplate,
    refresh,
    setSearch,
    search,
    setFilter,
    filter,
    debouncedSearch,
    handleCreateIssueTemplate,
    handleUpdateIssueTemplate,
  };
};

interface UseIssueTemplatesProps {
  initialFilter?: object;
}
